import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, User as UserIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/layout/Logo";
import TermsModal from "@/components/ui/TermsModal";
import { CARRIAGE_TERMS_TEXT } from "@/data/legalContent";

const PORTAL_LABELS = { admin: "Admin Portal", client: "Client Portal" };

// `portal` is set only on the dedicated /admin-login and /client-login entry
// points (the installable home-screen apps' start_urls); the generic /login
// renders unlabelled, as before.
export default function Login({ portal }) {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // A home-screen app opens straight on its login route, so someone with a
  // still-valid session shouldn't have to sign in again on every launch — same
  // behaviour as DriverLogin.
  if (!loading && user) {
    return <Navigate to={user.role === "driver" ? "/driver-dashboard/runs" : "/dashboard"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      if (u.role === "driver") {
        nav("/driver-dashboard");
      } else {
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white relative overflow-hidden">

      {/* ── Full-screen background image ────────────────────── */}
      <img
        src="/assets/drivncont.webp"
        alt="DRIVN FREIGHT"
        className="absolute inset-0 w-full h-full object-cover z-0 slide-down pointer-events-none"
      />
      {/* Very subtle dark overlay */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: "rgba(10,10,15,0.22)" }}
      />

      <header className="relative z-10 px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between max-w-[1500px] mx-auto">
        <Link to="/" className="shrink-0"><Logo size={40} /></Link>
        {/* Vertically centred on the DRIVN FREIGHT logo — top:0 cancels the nav-link's global -11px lift */}
        <nav className="hidden md:flex items-center gap-12">
          <Link to="/" className="nav-link" style={{ top: 0 }}>Home</Link>
          <Link to="/about" className="nav-link" style={{ top: 0 }}>About</Link>
          <Link to="/services" className="nav-link" style={{ top: 0 }}>Services</Link>
          <Link to="/login" className="nav-link active" style={{ top: 0 }} data-testid="client-login-link">Client Login</Link>
        </nav>
        <Link to="/login" className="pill-btn hidden md:block" style={{ background: "#CAF92C" }}>LOGIN</Link>
      </header>

      <div className="relative z-10 grid lg:grid-cols-2 gap-12 max-w-[1500px] mx-auto px-4 sm:px-8 pt-6 pb-16 sm:pb-24 items-center">
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="login-form">
          {PORTAL_LABELS[portal] && (
            <p className="text-xs uppercase tracking-[0.3em] text-white/70 mb-4" data-testid="login-portal-label">
              {PORTAL_LABELS[portal]}
            </p>
          )}
          <h1 className="brand-title rqnd-pro text-4xl sm:text-5xl md:text-6xl leading-tight">
            WELCOME<br /><span style={{ color: "#CAF92C" }}>BACK!</span>
          </h1>
          <p className="text-white/70 mt-6 text-lg">Please enter your login details.</p>

          <div className="mt-10 space-y-5">
            <div className="relative">
              <UserIcon className="absolute left-5 top-1/2 -translate-y-1/2 text-white/70 pointer-events-none z-10" size={18} />
              <input
                type="email"
                placeholder="Username / Email"
                className="df-input has-icon-left glass"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-white/70 pointer-events-none z-10" size={18} />
              <input
                type={show ? "text" : "password"}
                placeholder="Password"
                className="df-input has-icon-left has-icon-right glass"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-white/60 hover:text-white z-10"
                aria-label="toggle password"
              >
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="accent-[#19e6c8] w-4 h-4"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                Remember me
              </label>
              <a href="#" className="text-white/85 underline">Forgot Password</a>
            </div>

            <p className="text-xs text-white/60">
              By logging in, you agree to our{" "}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="text-red-500 hover:text-red-400 font-semibold"
                data-testid="terms-link"
              >
                Terms and Conditions of Carriage
              </button>.
            </p>

            <button
              type="submit"
              disabled={busy}
              className="pill-btn w-44 block mt-6"
              data-testid="login-submit"
            >
              {busy ? "..." : "LOGIN"}
            </button>

            <div className="pt-4 border-t border-white/10 mt-2">
              <p className="text-white/40 text-xs mb-2">Are you a driver?</p>
              <Link
                to="/driver-login"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#f59e0b] hover:text-[#fb923c] transition-colors"
              >
                Go to Driver Portal →
              </Link>
            </div>
          </div>
        </form>
      </div>

      {showTerms && (
        <TermsModal
          title="Terms and Conditions of Carriage"
          text={CARRIAGE_TERMS_TEXT}
          onClose={() => setShowTerms(false)}
        />
      )}
    </div>
  );
}
